import { useQuery } from '@apollo/client';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useWatchDebounce } from '@openx/utils/hooks/useWatchDebounce';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GetEnrichEstimateDocument } from '../../../../../graphql/query/audiences/GetEnrichEstimate.generated';
import { AudienceFields, type AudienceForm, type GroupLike } from '../../types';
import { filterNormalizedEmptyGroups } from '../../utils/filterNormalizedEmptyGroups';
import { hasSegmentDataInIncludes } from '../../utils/hasSegmentDataInIncludes';
import { hasAnyNonUsMarketGroupInNormalizedDefinition } from '../../utils/marketOperatorRestrictions';
import { Reach } from './Reach';
import { SourceIdReach } from './SourceIdReach';

export const EstimatedReach = () => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext<AudienceForm>();

  const observableNormalizedDefinition = useWatchDebounce({
    name: AudienceFields.NormalizedDefinition,
    control,
    debounceMs: 500,
  });
  const observableExportType = getValues(AudienceFields.ExportType);

  const hasSegmentData = useMemo(
    () => hasSegmentDataInIncludes(observableNormalizedDefinition),
    [observableNormalizedDefinition]
  );

  const normalizedDefinition = useMemo(
    () => filterNormalizedEmptyGroups(observableNormalizedDefinition),
    [observableNormalizedDefinition]
  );

  const isCustomExportSelected = observableExportType !== null;

  const hasAnyNonUsSegments = useMemo(() => {
    return hasAnyNonUsMarketGroupInNormalizedDefinition(observableNormalizedDefinition);
  }, [observableNormalizedDefinition]);

  const stripUUID = (obj: AudienceForm['normalized_definition']) => {
    const stripGroup = (group: GroupLike): GroupLike => {
      if ('groups' in group) {
        const { uuid: _uuid, operator, groups } = group;

        return {
          operator,
          groups: groups.map(stripGroup),
        };
      }

      const { uuid: _uuid, ...rest } = group;

      return rest;
    };

    return {
      excludes: {
        operator: obj.excludes.operator,
        groups: obj.excludes.groups.map(stripGroup),
      },
      includes: {
        operator: obj.includes.operator,
        groups: obj.includes.groups.map(stripGroup),
      },
    };
  };

  const {
    loading,
    error,
    data: {
      enrichEstimate: {
        reachTotal: legacyReachTotal = 0, // deprecated, can be removed after OA-117
        oaid: { reachTotal: OAID = 0, error: OAIDError = false } = {},
        device: { reachTotal: device = 0, error: deviceError = false } = {},
        liveintent: { reachTotal: liveintent = 0, error: liveintentError = false } = {},
        idl: { reachTotal: IDL = 0, error: IDLError = false } = {},
        cookie: { reachTotal: cookie = 0, error: cookieError = false } = {},
        id5: { reachTotal: ID5 = 0, error: ID5Error = false } = {},
        ip: { reachTotal: IP = 0, error: IPError = false } = {},
      },
    } = { enrichEstimate: {} },
  } = useQuery(GetEnrichEstimateDocument, {
    skip: !hasSegmentData,
    variables: { normalized_definition: stripUUID(normalizedDefinition), export_type: observableExportType },
  });

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 3,
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
        zIndex: 3,
        backgroundColor: 'background.boxLight',
        padding: 3,
        marginBottom: 3,
        borderBottom: (theme: Theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="h3">{t('Estimates')}</Typography>

      {!hasSegmentData ? (
        <Typography data-test="default-reach-title" variant="body1">
          {t('No data available.')}
        </Typography>
      ) : (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography data-test="estimated-reach-title" variant="body2Bold" sx={{ color: 'text.secondary' }}>
              {t('Estimated Unique User Reach')}
            </Typography>

            {!isCustomExportSelected && (
              <Tooltip data-test="estimated-reach-tooltip" title={t('Add segments below for reach estimates')}>
                <IconButton size="large">
                  <InfoRoundedIcon />
                </IconButton>
              </Tooltip>
            )}

            {hasAnyNonUsSegments && (
              <Tooltip
                data-test="estimated-reach-tooltip-non-us-segments"
                title={t(
                  'Notice: Unique Users reach estimates are based on the OpenX ID Graph, which currently supports US traffic only. When using non-US segments, these estimates may be inaccurate or equal 0. Please use Source IDs reach estimates instead.'
                )}
              >
                <IconButton size="large">
                  <InfoRoundedIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Reach
            OAID={OAID}
            OAIDError={OAIDError}
            legacyReachTotal={legacyReachTotal}
            fetchingError={error}
            isFetchingEstimates={loading}
            isIncludesEmpty={!hasSegmentData}
          />

          {!error && (
            <SourceIdReach
              isCustomExportSelected={isCustomExportSelected}
              loading={loading}
              cookie={cookie}
              cookieError={cookieError}
              device={device}
              deviceError={deviceError}
              IDL={IDL}
              IDLError={IDLError}
              liveintent={liveintent}
              liveintentError={liveintentError}
              ID5={ID5}
              ID5Error={ID5Error}
              IP={IP}
              IPError={IPError}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
